export type AdminPermissionKey =
  | "dashboard" | "admins" | "users" | "products" | "categories"
  | "orders" | "coupons" | "banners" | "reviews" | "notifications"
  | "support" | "settings" | "profile";
export type AdminRole = "superadmin" | "manager" | "support";
export type AdminPermissionState = { name: AdminPermissionKey; isActive: boolean };

export const ADMIN_PERMISSIONS: Array<{ key: AdminPermissionKey; label: string; description: string }> = [
  { key: "dashboard", label: "Dashboard", description: "View dashboard analytics" },
  { key: "admins", label: "Admin Management", description: "Create admins and manage permissions" },
  { key: "users", label: "Users", description: "View and manage customer accounts" },
  { key: "products", label: "Products", description: "View and manage products" },
  { key: "categories", label: "Categories", description: "View and manage categories" },
  { key: "orders", label: "Orders", description: "View and manage orders" },
  { key: "coupons", label: "Coupons", description: "Manage discount coupons" },
  { key: "banners", label: "Banners", description: "Manage storefront banners" },
  { key: "reviews", label: "Reviews", description: "Manage product reviews" },
  { key: "notifications", label: "Notifications", description: "Send customer notifications" },
  { key: "support", label: "Support", description: "Manage customer support tickets" },
  { key: "settings", label: "Settings", description: "Manage app settings" },
  { key: "profile", label: "Profile", description: "Update own profile and password" },
];
export const ASSIGNABLE_PERMISSIONS = ADMIN_PERMISSIONS.filter(({ key }) => key !== "profile");

export const ROUTE_PERMISSIONS: Record<string, AdminPermissionKey> = {
  "/dashboard": "dashboard", "/admins": "admins", "/add-admins": "admins",
  "/users": "users", "/products": "products", "/add-products": "products",
  "/products/edit": "products", "/categories": "categories", "/add-categories": "categories",
  "/orders": "orders", "/coupons": "coupons", "/banners": "banners",
  "/reviews": "reviews", "/notifications": "notifications", "/support": "support",
  "/settings": "settings", "/profile": "profile",
};

export const getAdminProfile = (): any => {
  try { const stored = localStorage.getItem("adminProfile"); return stored ? JSON.parse(stored) : null; }
  catch { return null; }
};
export const getAdminRole = (): AdminRole | null => getAdminProfile()?.role || null;
export const isSuperAdmin = (): boolean => getAdminRole() === "superadmin";
export const hasPermission = (key: AdminPermissionKey): boolean => {
  if (key === "profile" || isSuperAdmin()) return true;
  return (getAdminProfile()?.permissions || []).some((p: any) => p.name === key && p.isActive === true);
};
export const canManageAdmins = (): boolean => isSuperAdmin() || hasPermission("admins");
export const canManageUsers = (): boolean => hasPermission("users");
export const getEnabledPermissionKeys = (permissions: AdminPermissionState[] = []) =>
  permissions.filter((p) => p.isActive && p.name !== "profile").map((p) => p.name);
export const buildPermissionState = (permissions: any[] = []): AdminPermissionState[] => {
  const active = new Set(permissions.filter((p) => p?.isActive).map((p) => p.name));
  return ADMIN_PERMISSIONS.map(({ key }) => ({ name: key, isActive: key === "profile" || active.has(key) }));
};
export const buildAssignablePermissionState = (enabled: AdminPermissionKey[] = ["dashboard"]): AdminPermissionState[] =>
  ADMIN_PERMISSIONS.map(({ key }) => ({ name: key, isActive: key === "profile" || enabled.includes(key) }));
export const canAccessRoute = (pathname: string): boolean => {
  const matched = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length).find((route) => pathname === route || pathname.startsWith(`${route}/`));
  return !matched || hasPermission(ROUTE_PERMISSIONS[matched]);
};
export const getFirstAccessibleRoute = (): string =>
  ["/dashboard", ...Object.keys(ROUTE_PERMISSIONS).filter((route) => route !== "/profile")].find(canAccessRoute) || "/profile";
